import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import OwBadge from "../../../shared/OwBadge/OwBadge";
import OwButton from "../../../shared/OwButton/OwButton";
import { OwAccordion } from "../../../shared/OwAccordion/OwAccordion";
import SupplierContext from "../../../../contexts/SupplierContext";
import logo from "../../../../assets/images/stripe/stripe-logo.svg";
import OwToggle from "../../../shared/OwToggle/OwToggle";
import IUpdateSupplierIntegrationsDTO from "../../../../models/DTO/IUpdateSupplierIntegrationsDTO";
import UserContext from "../../../../contexts/UserContext";
import EndpointsEnum from "../../../../models/EndpointsEnum";
import IErrorDTO from "../../../../models/DTO/IErrorDTO";
import INotification from "../../../../models/INotification";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import { OwFnInput } from "../../../shared/OwFnInput/OwFnInput";
import { Socket } from "socket.io-client";
import { OwIconTooltip } from "@components/shared/OwIconTooltip/OwIconTooltip";

type Props = {
    socket: Socket;
    setActivityIndicator: (activityIndicator: boolean) => void;
    notifyFn: (data: IErrorDTO | null, _notification?: INotification, isFormatted?: boolean) => void;
    isMainViewLight: boolean;
};

export const StripeConnect: React.FC<Props> = (props) => {
    const supplierModel = useContext(SupplierContext);
    const userModel = useContext(UserContext);

    const [publishableApiKey, setPublishableApiKey] = useState<string | undefined | null>(
        supplierModel.Supplier_Integration.stripe_publishable_api_key
    );
    const [secretApiKey, setSecretApiKey] = useState<string | undefined>();
    const [webhookSecret, setWebhookSecret] = useState<string | undefined>();

    useEffect(() => {
        props.socket.on(EndpointsEnum.SupplierOnUpdateSupplierIntegrations, onUpdateSupplierIntegrations);

        return () => {
            props.socket.removeListener(EndpointsEnum.SupplierOnUpdateSupplierIntegrations, onUpdateSupplierIntegrations);
        };
    }, []);

    useEffect(() => {
        setPublishableApiKey(supplierModel.Supplier_Integration.stripe_publishable_api_key);
    }, [supplierModel.Supplier_Integration.stripe_publishable_api_key]);

    const toggleStripe = () => {
        const payload: IUpdateSupplierIntegrationsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            // @ts-expect-error
            Supplier_Integration: {
                has_stripe: !supplierModel.Supplier_Integration.has_stripe,
            },
        };

        props.socket.emit(EndpointsEnum.SupplierUpdateSupplierIntegrations, payload);
    };

    const onUpdateSupplierIntegrations = () => {};

    const handleChange = (e: ChangeEvent<any>) => {
        const value = e.target.value;
        const name = e.target.name;

        switch (name) {
            case "stripe_secret_api_key":
                setSecretApiKey(value);
                break;

            case "stripe_publishable_api_key":
                setPublishableApiKey(value);
                break;

            case "stripe_webhook_secret":
                setWebhookSecret(value);
                break;
        }
    };

    const saveUpdate = () => {
        const payload: IUpdateSupplierIntegrationsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            // @ts-expect-error
            Supplier_Integration: {
                stripe_publishable_api_key: publishableApiKey,
            },
        };

        if (webhookSecret?.length) {
            payload.Supplier_Integration.stripe_webhook_secret = webhookSecret;
        }

        if (secretApiKey?.length) {
            payload.Supplier_Integration.stripe_secret_api_key = secretApiKey;
        }

        props.socket.emit(EndpointsEnum.SupplierUpdateSupplierIntegrations, payload);
    };

    return (
        <div className={"StripeConnect"}>
            <OwAccordion
                hideChevron={!supplierModel.Supplier_Integration.has_stripe}
                leftImage={logo}
                leftImageHeight={30}
                titleClick={supplierModel.Supplier_Integration.has_stripe}
                rightElem={
                    <div className={"ms-auto"}>
                        <OwToggle
                            active={!!supplierModel.Supplier_Integration.has_stripe}
                            name={"toggleStripe"}
                            fn={toggleStripe}
                            label={""}
                            showDisableConfirm={true}
                        />
                    </div>
                }>
                <div>
                    <div>
                        <OwLabel title={"publishable_api_key"} />
                        <OwFnInput fn={handleChange} name={"stripe_publishable_api_key"} value={publishableApiKey} />
                    </div>

                    <div className={"mt-2"}>
                        <div className={"d-flex align-items-center"}>
                            <OwLabel title={"secret_api_key"} className={"me-2"} />
                            {supplierModel.Supplier_Integration.stripe_secret_api_key === "true" ? (
                                <OwLabel className={"text-success"} title={"is_set"} leftIconClass={"fas fa-check"} />
                            ) : (
                                <OwLabel className={"text-danger"} title={"not_set"} leftIconClass={"fas fa-times"} />
                            )}
                        </div>
                        <OwFnInput fn={handleChange} name={"stripe_secret_api_key"} value={secretApiKey} />
                    </div>

                    <div className={"mt-2"}>
                        <div className={"d-flex align-items-center"}>
                            <OwLabel title={"webhook_secret"} />
                            <OwIconTooltip
                                iconClassName="ms-2 me-2 text-muted fas fa-info-circle"
                                tooltipContent="stripe_webhook_secret_tooltip"
                                translateContent={true}
                            />
                            <div>
                                {supplierModel.Supplier_Integration.stripe_webhook_secret === "true" ? (
                                    <OwLabel className={"text-success"} title={"is_set"} leftIconClass={"fas fa-check"} />
                                ) : (
                                    <OwLabel className={"text-danger"} title={"not_set"} leftIconClass={"fas fa-times"} />
                                )}
                            </div>
                        </div>

                        <OwFnInput fn={handleChange} name={"stripe_webhook_secret"} value={webhookSecret} />
                    </div>

                    <div className={"mt-2"}>
                        <OwButton title={"save"} fn={saveUpdate} />
                    </div>
                </div>
            </OwAccordion>
        </div>
    );
};
